<template>
  <v-form v-model="valid" ref="form" @submit.prevent="submit">
    <v-card>
      <v-card-text>
        <!-- Basic Info -->
        <div class="subheading mb-3">{{ $t("label.basicInfo") }}</div>
        <v-layout row>
          <v-flex xs3 lg2>
            <label class="form-label">{{ $t("label.creator") }}</label>
          </v-flex>
          <v-flex xs9 lg10>
            <span class="form-value">{{ form.creator | personToString }}</span>
          </v-flex>
        </v-layout>
        <v-layout row v-if="showArea">
          <v-flex xs3 lg2>
            <label class="form-label">{{ $t("label.area") }}</label>
          </v-flex>
          <v-flex xs9 lg10>
            <span class="form-value">{{ area }}</span>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs3 lg2>
            <label class="form-label">{{ $t("label.system") }}*</label>
          </v-flex>
          <v-flex xs9 lg10>
            <v-select
              autocomplete
              :disabled="disabledWorkflow"
              v-bind:items="systems"
              v-model="form.system.id"
              :rules="validation.required"
              :no-data-text="$t('message.warning.noData')"
            ></v-select>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs3 lg2>
            <label class="form-label">{{ $t("label.flow") }}*</label>
          </v-flex>
          <v-flex xs9 lg10>
            <v-select
              autocomplete
              :disabled="disabledWorkflow"
              v-bind:items="demandTypes"
              v-model="form.type.id"
              :rules="validation.required"
              :no-data-text="$t('message.warning.noData')"
            ></v-select>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs3 lg2>
            <label class="form-label">{{ $t("label.requester") }}*</label>
          </v-flex>
          <v-flex xs9 lg10>
            <v-select
              autocomplete
              :disabled="enableAdmin"
              v-bind:items="persons"
              v-model="form.requester.id"
              :rules="validation.required"
              :no-data-text="$t('message.warning.noData')"
            ></v-select>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs3 lg2>
            <label class="form-label">{{ $t("label.contract") }}*</label>
          </v-flex>
          <v-flex xs9 lg10>
            <v-select
              autocomplete
              :disabled="enableContract"
              v-bind:items="contracts"
              v-model="form.contract.id"
              v-on:change="form.contractItem.id = null"
              :rules="validation.required"
              :no-data-text="$t('message.warning.noData')"
              :maxlength="constants.LENGTH_CONTRACT"
            ></v-select>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs3 lg2>
            <label class="form-label">{{ $t("label.contractItem") }}*</label>
          </v-flex>
          <v-flex xs7 lg6>
            <v-select
              autocomplete
              :disabled="enableContract"
              v-bind:items="contractItems"
              v-model="form.contractItem.id"
              :rules="validation.required"
              :no-data-text="$t('message.warning.noData')"
              :maxlength="constants.LENGTH_CONTRACT_ITEM"
            ></v-select>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs3 lg2>
            <label class="form-label">{{ $t("label.relatedDemand") }}</label>
          </v-flex>
          <v-flex xs9 lg10>
            <v-select
              autocomplete
              :disabled="enableAdmin"
              v-bind:items="demands"
              v-model="form.relatedDemand.id"
              @input="verifyDemand"
              clearable
              :no-data-text="$t('message.warning.noData')"
              :maxlength="constants.LENGTH_DEMAND"
            ></v-select>
          </v-flex>
        </v-layout>

        <!-- Details -->
        <div class="subheading mt-3">{{ $t("label.details") }}</div>
        <p v-html="$t('message.info.demandCreationDetails')"></p>
        <v-layout row>
          <v-flex xs3 lg2>
            <label class="form-label">{{ $t("label.title") }}*</label>
          </v-flex>
          <v-flex xs9 lg10>
            <v-text-field
              :disabled="disabled"
              v-model="form.title"
              :rules="validation.text"
              :counter="constants.LENGTH_TITLE"
              :maxlength="constants.LENGTH_TITLE"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout row class="mb-4">
          <v-flex xs3 lg2>
            <label class="form-label">{{ $t("label.description") }}*</label>
          </v-flex>
          <v-flex xs9 lg10>
            <v-text-field
              multi-line
              :disabled="disabled"
              v-model="form.description"
              :rules="validation.text"
              auto-grow
              :counter="constants.LENGTH_DESCRIPTION"
              :maxlength="constants.LENGTH_DESCRIPTION"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout row class="mb-4">
          <v-flex xs3 lg2>
            <label class="form-label">{{ $t("label.desirableDate") }}</label>
          </v-flex>
          <v-flex xs2 lg2>
            <app-menu-date-picker
              :model.sync="form.desirableDate"
              :disabled="disabled"
              :label="$t('label.desirableDate')"
            ></app-menu-date-picker>
          </v-flex>
        </v-layout>
        <v-layout row class="mb-4">
          <v-flex xs3 lg2>
            <label class="form-label">{{ $t("label.remarks") }}</label>
          </v-flex>
          <v-flex xs9 lg10>
            <v-text-field
              multi-line
              :disabled="disabled"
              v-model="form.remarks"
              auto-grow
              :counter="constants.LENGTH_REMARKS"
              :maxlength="constants.LENGTH_REMARKS"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout row class="mb-4">
          <v-flex xs3 lg2>
            <label class="form-label">{{ $t("label.reason") }}</label>
          </v-flex>
          <v-flex xs9 lg10>
            <v-text-field
              multi-line
              :disabled="disabled"
              v-model="form.reason"
              auto-grow
              :counter="constants.LENGTH_REASON"
              :maxlength="constants.LENGTH_REASON"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout row class="mb-4">
          <v-flex xs3 lg2>
            <label class="form-label">{{ $t("label.outcome") }}</label>
          </v-flex>
          <v-flex xs9 lg10>
            <v-text-field
              multi-line
              :disabled="disabled"
              v-model="form.outcome"
              auto-grow
              :counter="constants.LENGTH_OUTCOME"
              :maxlength="constants.LENGTH_OUTCOME"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout row>
          <v-flex xs3 lg2>
            <label class="form-label">{{
              $t("label.similarDemandsDescription")
            }}</label>
          </v-flex>
          <v-flex xs9 lg10>
            <v-text-field
              :disabled="disabled"
              v-model="form.similarDemandsDescription"
              :counter="constants.LENGTH_SIMILAR_DEMANDS"
              :maxlength="constants.LENGTH_SIMILAR_DEMANDS"
            ></v-text-field>
          </v-flex>
        </v-layout>

        <!-- Files -->
        <div v-if="form.files.length > 0">
          <div class="subheading mt-3">{{ $t("label.files") }}</div>
          <p v-html="$t('message.info.demandFiles')"></p>
          <v-layout row>
            <v-flex xs3 lg2>
              <label class="form-label">{{
                $t("label.filesDescription")
              }}</label>
            </v-flex>
            <v-flex xs9 lg10>
              <v-text-field
                multi-line
                :disabled="disabled"
                v-model="form.filesDescription"
                auto-grow
                :counter="constants.LENGTH_FILES_DESCRIPTION"
                :maxlength="constants.LENGTH_FILES_DESCRIPTION"
              ></v-text-field>
            </v-flex>
          </v-layout>
          <v-layout row>
            <v-flex xs9 lg10 offset-xs3 offset-lg2 class="mt-3">
              <app-files
                :files.sync="form.files"
                :removedFiles.sync="form.removedFiles"
                :removeable="!disabled"
              ></app-files>
            </v-flex>
          </v-layout>
        </div>
      </v-card-text>

      <v-card-actions
        v-if="!disabled || canCancel || !disabledContract || enableAdmin"
      >
        <v-btn
          v-if="canRequest"
          :disabled="this.saving || this.canceling"
          :loading="processing"
          @click="advance"
          color="primary"
        >
          {{ $t("label.requestApproval") }}
        </v-btn>

        <app-file-upload
          v-if="!disabled"
          :disabled="this.saving || this.processing || this.canceling"
          :model.sync="form.files"
        >
        </app-file-upload>

        <v-spacer></v-spacer>

        <v-btn
          v-if="!enableAdmin"
          :disabled="this.processing || this.canceling"
          :loading="saving"
          @click="save"
        >
          {{ $t("label.save") }}
        </v-btn>

        <v-btn
          v-if="canCancel"
          :disabled="this.saving || this.processing"
          :loading="canceling"
          @click="cancel"
          color="error"
        >
          {{ $t("label.cancel") }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-layout row wrap v-if="canceling" class="mt-3">
      <v-flex>
        <app-cancel
          :message="$t('message.info.demandCancelRequest')"
          :isCancelling.sync="canceling"
        ></app-cancel>
      </v-flex>
    </v-layout>
  </v-form>
</template>

<script>
import constants from "@/helpers/constants";
import utilities from "@/helpers/utilities";
import validation from "@/helpers/validation";
import FileUpload from "@/components/shared/FileUpload";
import Files from "@/components/shared/Files";
import Cancel from "@/components/demands/shared/Cancel";
import MenuDatePicker from "@/components/shared/MenuDatePicker";
import Demand from "@/helpers/entities/Demand";

export default {
  data() {
    return {
      constants,
      validation,
      valid: true,
      saving: false,
      processing: false,
      canceling: false,
      form: Demand.build(),
      persons: this.$store.getters.personsValueText,
      systems: this.$store.getters.activeSystemsValueText,
      demands: this.$store.getters.demandsValueText,
    };
  },
  computed: {
    area() {
      if (this.form.requester.id > 0) {
        const person =
          this.$store.getters.personsDictionary[this.form.requester.id];
        if (person.isHired || !person.area) return "";

        const area = this.$store.getters.areasDictionary[person.area.id];
        return `${area.acronym} - ${area.name}`;
      }
      return "";
    },
    contractItems() {
      return this.$store.getters.contractItemsValueText.filter(
        (item) => item.contractId === this.form.contract.id
      );
    },
    contracts() {
      return utilities.combobox(
        this.$store.getters.contractsValueText,
        this.form.contract.id
      );
    },
    demand() {
      return this.$store.getters.demand;
    },
    demandTypes() {
      return utilities.combobox(
        this.$store.getters.demandTypesValueText,
        this.form.type.id
      );
    },
    canRequest() {
      return this.demand.status.id <= constants.STATUS_CREATED;
    },
    canCancel() {
      // Demand hasn't been saved yet, so there's nothing to cancel
      if (utilities.isEmptyOrZero(this.demand.id)) return false;

      const creatorId = this.$store.getters.personsDictionary[this.form.creator.id].id;
      const requesterId = this.$store.getters.personsDictionary[this.form.requester.id].id;
      const userLoggedId = this.$store.getters.profile.person.id;
      const ownerId = this.$store.getters.demand.owner.id;

      // Creator of a demand can cancel demand until status id 1 ("Aberta")
      if (creatorId === userLoggedId && this.demand.status.id === 1) {
        return true;
      }

      // Requester of a demand can cancel demand until status id 1 ("Aberta")
      if (requesterId === userLoggedId && this.demand.status.id === 1) {
        return true;
      }

      if ((ownerId === userLoggedId && this.demand.status.id === 1) ||
          (ownerId === userLoggedId && this.demand.status.id === 2)) {
        return true;
      }

      if (this.demand.subOwners) {
        /* eslint max-len: ["error", { "code": 200 }] */
        if ((this.demand.subOwners.filter(owner => owner.id === this.$store.getters.profile.person.id).length === 0) === false && this.demand.status.id === 2) {
          return true;
        }
      }

      // Demand is already done or rejected, can't be canceled
      if (
        this.demand.status.id === constants.STATUS_DONE ||
        this.demand.status.id === constants.STATUS_REJECTED
      ) {
        return false;
      }

      // Logged user is an prodam admin
      if (this.$store.getters.isAdmin && this.$store.getters.profile.isHired) {
        return true;
      }

      // Logged user is a root user.
      if (this.$store.getters.profile.isRoot) {
        return true;
      }

      // Logged user or creator is a contrator, only allow cancel if they are the same person
      if (
        this.$store.getters.profile.person.isHired ||
        this.demand.creator.isHired
      ) {
        return this.demand.creator.id === this.$store.getters.profile.person.id;
      }

      // Logged user is not an admin of his office.
      if (!this.$store.getters.isAdmin) {
        return false;
      }

      try {
        // User is a employee of the city. Allow canceling if he
        // belongs to the same secretary as the creator
        const areas = this.$store.getters.areasDictionary;
        const creatorOffice = areas[this.demand.creator.area.id].office.id;
        const userOffice =
          areas[this.$store.getters.profile.person.area.id].office.id;

        return creatorOffice === userOffice;
      } catch (e) {
        return true;
      }
    },
    disabled() {
      return (
        this.demand.status.id >= constants.STATUS_PLANNING ||
        (this.demand.owner.id !== this.$store.getters.profile.person.id &&
          !this.$store.getters.isAdmin)
      );
    },
    disabledWorkflow() {
      return (
        this.demand.status.id >= constants.STATUS_CREATED_APPROVAL ||
        (this.demand.owner.id !== this.$store.getters.profile.person.id &&
          !this.$store.getters.isAdmin)
      );
    },
    disabledContract() {
      return (
        this.demand.status.id !== constants.STATUS_CREATED_APPROVAL ||
        this.demand.status.id == null ||
        (this.demand.owner.id !== this.$store.getters.profile.person.id &&
          !this.$store.getters.isAdmin)
      );
    },
    showArea() {
      return !utilities.isEmptyOrZero(this.area);
    },
    isAdmin() {
      const ok = this.$store.getters.isAdmin;
      return ok;
    },
    enableAdmin() {
      if (this.$store.getters.isAdmin) {
        return !this.$store.getters.isAdmin;
      }
      return this.disabled;
    },
    enableContract() {
      if (
        this.demand.status.id !== constants.STATUS_CREATED_APPROVAL &&
        this.$store.getters.isAdmin
      ) {
        this.disabledContract = false;
        return false;
      }
      return this.disabledContract;
    },
  },

  components: {
    appFileUpload: FileUpload,
    appFiles: Files,
    appCancel: Cancel,
    appMenuDatePicker: MenuDatePicker,
  },

  beforeMount() {
    this.copyToForm();
  },

  watch: {
    demand: "copyToForm",
    "form.system.id": "chooseContract",
  },
  mounted() {
    setTimeout(() => {
      this.demands = this.$store.getters.relatedDemandsValueText;
    }, 600);
  },
  methods: {
    chooseContract(value, oldValue) {
      // This condition happens when the user opens the page to edit a demand
      if (oldValue === null && this.demand.status.id) return;

      const system = this.$store.getters.systemsDictionary[value];
      // If system has a contract return it, else return default contract.
      const contract = system.contract || this.$store.getters.contracts[0];
      this.form.contract.id = contract.id || 0;
      // If system has a contract item return it, else return the first item in the contract
      this.form.contractItem.id =
        (
          system.contractItem ||
          this.$store.getters.contractItems.filter(
            (item) => item.contract.id === this.form.contract.id
          )[0]
        ).id || 0;
    },
    copyToForm() {
      this.form = Demand.copy(this.demand);
      if (this.form.code === 0) {
        this.form.requester.id = 0;
      }
    },
    verifyDemand(value) {
      if (value === this.demand.id) {
        this.form.relatedDemand.id = 0;
        this.$store.dispatch("showError", "Não é possível relacionar a demanda a ela mesma.");
      }
    },
    async save() {
      if (!this.$refs.form.validate()) {
        this.$store.dispatch("showError", this.$t("message.error.formError"));
        return;
      }

      this.saving = true;
      await this.$store.dispatch(
        "saveOrUpdateDemand",
        Demand.prepare(this.form)
      );
      this.saving = false;
    },
    async advance() {
      if (!this.$refs.form.validate()) {
        this.$store.dispatch("showError", this.$t("message.error.formError"));
        return;
      }
      this.processing = true;
      await this.$store.dispatch(
        "saveOrUpdateDemand",
        Demand.prepare(this.form)
      );
      this.processing = false;
      await this.$store.dispatch("requestDemand", this.$store.getters.demand);
      this.$router.push({ name: "dashboard" });
    },
    async cancel() {
      this.canceling = true;
      // await this.$store.dispatch('cancelDemand', this.form);
      // this.$router.push({ name: 'dashboard' });
    },
  },
};
</script>


// WEBPACK FOOTER //
// src/components/demands/steps/Creation.vue